import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookOpen, ListChecks } from 'lucide-react';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { cn, money, todayIso } from '@/lib/utils';
import { minorFromInput } from '@/components/MoneyInput';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Button, DatePicker, Field, Input, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { CONTROL_ACCOUNT_CODES, type Account } from './AccountingPage';

let lineSeq = 0;
const emptyLine = () => ({ key: `line-${(lineSeq += 1)}`, accountId: '', debit: '', credit: '' });

export function VoucherPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const toast = useToast();
  const queryClient = useQueryClient();
  const returnTo = safeReturnTo(params.get('returnTo'), '/accounting?tab=journals');
  const [entryDate, setEntryDate] = useState(todayIso());
  const [memo, setMemo] = useState('');
  const [lines, setLines] = useState([emptyLine(), emptyLine()]);
  const [error, setError] = useState<string | null>(null);

  const accounts = useQuery({
    queryKey: ['accounts'],
    queryFn: () => get<Account[]>('/accounting/accounts'),
  });

  const totals = lines.reduce(
    (acc, line) => ({
      debit: acc.debit + minorFromInput(line.debit),
      credit: acc.credit + minorFromInput(line.credit),
    }),
    { debit: 0, credit: 0 },
  );

  const post_ = useMutation({
    mutationFn: () =>
      post('/accounting/journals', {
        entryDate,
        memo: memo || undefined,
        lines: lines
          .filter((line) => line.accountId)
          .map((line) => ({
            accountId: line.accountId,
            debitMinor: minorFromInput(line.debit),
            creditMinor: minorFromInput(line.credit),
          })),
      }),
    onSuccess: () => {
      toast.success(tr('billing', 'voucher_posted'));
      void queryClient.invalidateQueries({ queryKey: ['journals'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const balanced = totals.debit > 0 && totals.debit === totals.credit;
  const dirty = !post_.isSuccess && (memo !== '' || entryDate !== todayIso() || lines.some((line) => line.accountId || line.debit || line.credit));

  return (
    <FormScreen
      title={tr('billing', 'manual_voucher')}
      description={tr('billing', 'debits_and_credits_must_balance_posted_entries')}
      eyebrow={tr('billing', 'accounting')}
      icon={BookOpen}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!balanced) return;
        setError(null);
        post_.mutate();
      }}
    >
      <FormSection title={tr('billing', 'voucher')} icon={BookOpen}>
        <Field label={tr('billing', 'date')} required>
          <DatePicker value={entryDate} max={todayIso()} onChange={setEntryDate} />
        </Field>
        <Field label={tr('billing', 'memo')}>
          <Input value={memo} onChange={(e) => setMemo(e.target.value)} />
        </Field>
      </FormSection>

      <FormSection title={tr('billing', 'lines')} icon={ListChecks} columns={1}>
        {lines.map((line, index) => (
          <div key={line.key} className="grid gap-2 sm:grid-cols-[1fr_140px_140px]">
            <Select value={line.accountId} onChange={(e) => setLines(lines.map((l, i) => (i === index ? { ...l, accountId: e.target.value } : l)))}>
              <option value="">{tr('billing', 'select_an_account')}</option>
              {accounts.data
                ?.filter((account) => account.isPostable && !CONTROL_ACCOUNT_CODES.includes(account.code))
                .map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.code} · {account.name}
                  </option>
                ))}
            </Select>
            <Input
              type="text"
              dir="ltr"
              inputMode="decimal"
              placeholder={tr('billing', 'debit')}
              value={line.debit}
              onChange={(e) => setLines(lines.map((l, i) => (i === index ? { ...l, debit: e.target.value, credit: '' } : l)))}
            />
            <Input
              type="text"
              dir="ltr"
              inputMode="decimal"
              placeholder={tr('billing', 'credit')}
              value={line.credit}
              onChange={(e) => setLines(lines.map((l, i) => (i === index ? { ...l, credit: e.target.value, debit: '' } : l)))}
            />
          </div>
        ))}
        <div>
          <Button type="button" variant="outline" size="sm" onClick={() => setLines([...lines, emptyLine()])}>
            {tr('billing', 'add_line')}
          </Button>
        </div>

        <div
          className={cn(
            'flex justify-between rounded-md border px-3 py-2 text-sm font-medium tabular',
            totals.debit === totals.credit ? 'border-success/40 text-success' : 'border-destructive/40 text-destructive',
          )}
        >
          <span>
            {tr('billing', 'debit')} {money(totals.debit)}
          </span>
          <span>
            {tr('billing', 'credit')} {money(totals.credit)}
          </span>
        </div>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel={tr('billing', 'post')} loading={post_.isPending} incomplete={!balanced} cancelTo={returnTo} />
    </FormScreen>
  );
}
